import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { productAPI, categoriesAPI, publicAPI } from '@/services/api';
import { getTranslatedName } from '@/utils/i18nHelpers';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { handleSubmit as submit } from "@/utils/submitUtils";
import CostSummary from "@/components/CostSummary";
import LocationEditor from "@/components/LocationEditor";
import ImageUpload from "@/components/ImageUpload";
import FeatureAdToggle from "@/components/FeatureAdToggle";
import SubmitButton from "@/components/SubmitButton";
import BackLink from "@/components/BackLink";
import { computeCosts, defaultPricing } from "@/utils/costUtil";
import WaiverCheckBox from "@/components/WaiverCheckBox";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const OTHER = '__other__';

export default function CreateProductAdPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
	const location = useLocation();
  const isClone = location.pathname.includes('clone');
	const { id: editId } = useParams();
  const [categories, setCategories] = useState([]);
	const [customCategory, setCustomCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
	const [pricing, setPricing] = useState(defaultPricing());
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);
  const [form, setForm] = useState({
		title: '',
		description: '',
		price: '',
		category: '',
		customCategory: '',
		location_lat: null,
		location_lng: null,
		location_name: '',
		is_featured: false,
		featured_paid: false,
		hidden_images: []
  });

  useEffect(() => {
		categoriesAPI.list()
			.then(r => setCategories(r.data || []))
			.catch(() => {});
		publicAPI.settings()
			.then(r => setPricing(
				prev => ({ ...prev, ...r.data })))
			.catch(() => {});
    if (editId) {
      productAPI.get(editId).then(r => {
        const ad = r.data;
        setForm({
          title: ad.title || '',
					description: ad.description || '',
					price: ad.price || '',
          category: ad.category || '',
					customCategory: ad.customCategory || '',
					location_lat: ad.location_lat || '',
					location_lng: ad.location_lng || '',
					location_name: ad.location_name || '',
					is_featured: ad.is_featured || false,
					featured_paid: ad.featured_paid || false,
					hidden_images: ad.hidden_images || [],
					included_images: ad.included_images || pricing.free_images_limit || 5,
        });
				setCustomCategory(ad.customCategory || '');
				// Load existing images as thumbnails
				const existingImages = (ad.images || []).map((url, i) => ({
					url,
					preview: url.startsWith('/') ? `${API_BASE}${url}` : url,
					thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${API_BASE}${ad.thumbnails[i]}` : ad.thumbnails[i]) : null,
					enabled: !(ad.hidden_images || []).includes(url),
					isExisting: true
				}));
				// need to remove images in case of cloning as I don't have the objects to upload
				setImages(isClone ? [] : existingImages);

			}).catch(() => navigate('/dashboard'));
    }
  }, [user, editId, navigate]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  	// --- Image handlers ---
	const handleImages = (e) => {
		const files = Array.from(e.target.files);
		const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false }));
		setImages(prev => [...prev, ...newImgs]);
	};
	const removeImage = (i) => {
		if (!images[i].isExisting) URL.revokeObjectURL(images[i].preview);
		setImages(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleImage = (i) =>
		setImages(prev => prev.map((img, idx) => idx === i ?
			{ ...img, enabled: !img.enabled } : img));

	const handleCategoryChange = (v) => {
		if (v === OTHER) {
			update('category', OTHER)
		} else {
			update('category', v)
			setCustomCategory('');
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.title || !form.description || !form.price || !form.category || !form.location_name) {
			toast.error(t('product.required_fields_error'));
			return;
		}

		const cost = computeCosts(images, [], pricing, form.is_featured, form.featured_paid, form.included_images,
			form.included_videos);

		const newImages = cost.enabledImages.filter(i => !i.isExisting);

		const hiddenImgs = images.filter(i => i.isExisting && !i.enabled).map(i => i.url);

		const payload = {
			...form,
			category: form.category,
			customCategory: customCategory,
			price: parseFloat(form.price),
			location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
			location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
			included_images: Math.max(pricing.free_images_limit, cost.enabledImages.length),
			hidden_images: hiddenImgs,
		};

		await submit(payload, productAPI, isClone ? null : editId, 'product', cost, newImages, [],
			form.is_featured, form.featured_paid, setLoading, t, navigate);
	};

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="create-product-ad-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

				<BackLink title={form.title} uri="/products" textKey='nav.back_to_results' />

        <h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-8">
          {isClone ? t('product.create_ad') : editId ? t('product.edit_ad') : t('product.create_ad')}
        </h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white border border-[#E6E2D6] rounded-sm p-6 space-y-4">
            <div>
              <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{t('product.category')} *
							</Label>
							<Select value={ form.category } onValueChange={ handleCategoryChange }>
								<SelectTrigger data-testid="product-category"
															 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11">
									<SelectValue placeholder={ t('product.category_placeholder') }/>
								</SelectTrigger>
								<SelectContent>
									{ categories.map(c =>
										<SelectItem key={ c.id } value={ c.name }>
											{ getTranslatedName(c, i18n.language) }
										</SelectItem>)
									}
									<SelectItem value={ OTHER }>{ t('horse.other') }</SelectItem>
								</SelectContent>
							</Select>
							{form.category === OTHER && (
								<Input data-testid="horse-custom-color" value={ customCategory }
											 onChange={ e => setCustomCategory(e.target.value) }
											 className="mt-2 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
											 placeholder={ t('product.custom_category_placeholder') }/>
							)}
            </div>
						<div>
							<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{ t('product.title') } *
							</Label>
							<Input data-testid="product-title" required value={ form.title }
										 onChange={ e => update('title', e.target.value) }
										 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
										 placeholder={ t('product.title_placeholder') }/>
						</div>
						<div>
							<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{ t('horse.description') } *
							</Label>
							<Textarea data-testid="product-description" required value={ form.description }
												onChange={ e => update('description', e.target.value) }
												className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm min-h-[100px]"/>
						</div>
						<div>
							<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{ t('horse.price') } (EUR)	*
							</Label>
							<Input data-testid="product-price" type="number" required value={ form.price }
										 onChange={ e => update('price', e.target.value) }
										 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"/>
						</div>
						<div>
							<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{ t('horse.location') }
							</Label>
							<Input data-testid="product-location" value={ form.location_name }
										 onChange={ e => update('location_name', e.target.value) }
										 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
										 placeholder={t('horse.location_placeholder')}/>
						</div>
          </div>

					{/* Location */}
					<LocationEditor
						location_name={ form.location_name }
						location_lat={ form.location_lat }
						location_lng={ form.location_lng }
						update={ update }/>

					{/* Images */}
					<ImageUpload images={ images }
											 pricing={ pricing }
											 handleImages={ handleImages }
											 removeImage={ removeImage }
											 toggleImage={ toggleImage } />

					{/* Featured Ad */}
					<FeatureAdToggle is_featured={ form.is_featured } pricing={ pricing }  update={ update } />

					{/* Cost Summary */}
					<CostSummary
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						t={ t }/>

					<WaiverCheckBox
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						state={ waivedWithdrawalRight }
						t={ t }/>

					<SubmitButton
						editId={ isClone ? null : editId }
						loading={ loading }
						label='product.create_ad'
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						waivedWithdrawalRight={ waivedWithdrawalRight }
					/>

        </form>
      </div>
    </div>
  );
}